/**
 * ChoiceSettingsPanel — Layout, position, timer and shuffle controls
 * for scene choice settings.
 *
 * Reads/writes via sceneStore.setChoiceSettings.
 */

import React, { useEffect, useState } from 'react';
import { useSceneStore } from '../../../stores/sceneStore';
import { previewDurationMs } from '../../../utils/sceneDuration';
import { parseShowBeforeEnd, parseTimerSeconds, type ParsedSetting } from './choiceSettingsParse';
import styles from './ChoiceSettingsPanel.module.scss';

interface NumberSettingProps {
  testId: string;
  value: number | undefined;
  parse: (text: string) => ParsedSetting;
  onCommit: (value: number | undefined) => void;
  min: number;
  max?: number;
  step: number | 'any';
  /** 0 means unset and shows as empty (the timer). */
  zeroIsEmpty?: boolean;
}

/**
 * A number field that stores only valid entries (spec 008): a valid entry is
 * stored as typed, an invalid one is not, and on blur the field shows the
 * stored value again.
 */
const NumberSetting: React.FC<NumberSettingProps> = ({ testId, value, parse, onCommit, min, max, step, zeroIsEmpty }) => {
  const stored = value === undefined || (zeroIsEmpty && value === 0) ? '' : String(value);
  const [text, setText] = useState(stored);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setText(stored);
  }, [stored, editing]);

  return (
    <input
      type="number"
      data-testid={testId}
      className={styles.timerInput}
      min={min}
      max={max}
      step={step}
      placeholder="—"
      value={text}
      aria-invalid={!parse(text).ok}
      onFocus={() => setEditing(true)}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = parse(e.target.value);
        if (parsed.ok && parsed.value !== value) onCommit(parsed.value);
      }}
      onBlur={() => {
        setEditing(false);
        setText(stored);
      }}
    />
  );
};

const ChoiceSettingsPanel: React.FC = () => {
  const choiceSettings = useSceneStore((s) => s.scene?.choiceSettings);
  const sceneLengthMs = useSceneStore((s) => previewDurationMs(s.scene));
  const setChoiceSettings = useSceneStore((s) => s.setChoiceSettings);

  const layout = choiceSettings?.layout ?? 'list';
  const position = choiceSettings?.position ?? 'bottom';
  const timer = choiceSettings?.timer;
  const shuffleOptions = choiceSettings?.shuffleOptions ?? false;
  const showBeforeEnd = choiceSettings?.showBeforeEnd;
  const appearsAtStart = (showBeforeEnd ?? 0) > 0 && showBeforeEnd! * 1000 >= sceneLengthMs;

  return (
    <div className={styles.settingsPanel}>
      <div className={styles.settingsHeader}>Settings</div>

      {/* Layout toggle */}
      <div className={styles.settingRow}>
        <label className={styles.settingLabel}>Layout</label>
        <div className={styles.toggleGroup}>
          <button
            className={`${styles.toggleButton} ${layout === 'list' ? styles.active : ''}`}
            onClick={() => setChoiceSettings({ layout: 'list' })}
          >
            List
          </button>
          <button
            className={`${styles.toggleButton} ${layout === 'grid' ? styles.active : ''}`}
            onClick={() => setChoiceSettings({ layout: 'grid' })}
          >
            Grid
          </button>
        </div>
      </div>

      {/* Position toggle */}
      <div className={styles.settingRow}>
        <label className={styles.settingLabel}>Position</label>
        <div className={styles.toggleGroup}>
          <button
            className={`${styles.toggleButton} ${position === 'bottom' ? styles.active : ''}`}
            onClick={() => setChoiceSettings({ position: 'bottom' })}
          >
            Bottom
          </button>
          <button
            className={`${styles.toggleButton} ${position === 'center' ? styles.active : ''}`}
            onClick={() => setChoiceSettings({ position: 'center' })}
          >
            Center
          </button>
        </div>
      </div>

      {/* Timer: a whole number 1–120, empty = no timer */}
      <div className={styles.settingRow}>
        <label className={styles.settingLabel}>Timer (s)</label>
        <NumberSetting
          testId="choice-timer-input"
          value={timer}
          parse={parseTimerSeconds}
          onCommit={(v) => setChoiceSettings({ timer: v })}
          min={1}
          max={120}
          step={1}
          zeroIsEmpty
        />
      </div>

      {/* Show before end: ≥ 0, decimals allowed */}
      <div className={styles.settingRow}>
        <label className={styles.settingLabel}>Show (s) before end</label>
        <NumberSetting
          testId="choice-show-before-end-input"
          value={showBeforeEnd}
          parse={parseShowBeforeEnd}
          onCommit={(v) => setChoiceSettings({ showBeforeEnd: v })}
          min={0}
          step="any"
        />
      </div>
      {appearsAtStart && (
        <div className={styles.settingHint} data-testid="choice-show-before-end-hint">Choices appear at the start</div>
      )}

      {/* Shuffle */}
      <div className={styles.settingRow}>
        <label className={styles.settingLabel}>
          <input
            type="checkbox"
            checked={shuffleOptions}
            onChange={(e) => setChoiceSettings({ shuffleOptions: e.target.checked })}
          />
          {' '}Shuffle options
        </label>
      </div>
    </div>
  );
};

export default ChoiceSettingsPanel;
